import { HelpCircle, X } from 'lucide-react';
import type { ClarificationState } from '@/types/chat';

interface Props {
  messageId: string;
  state: ClarificationState;
  onPick: (interpretation: string) => void;
  onDismiss: () => void;
  disabled?: boolean;
}

// ClarificationCard renders the PRD §13.2 clarification UX inline above
// an assistant message. User picks an option → we emit a new user
// message containing the chosen interpretation, letting the agent
// proceed without guessing.
export function ClarificationCard({ messageId, state, onPick, onDismiss, disabled }: Props) {
  const isResolved = state.status !== 'pending';

  return (
    <div
      key={messageId}
      className="w-full max-w-[95%] rounded-lg border border-amber-300 bg-amber-50 text-amber-900 shadow-card dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-100"
    >
      <div className="flex items-start gap-2 px-3 py-2">
        <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{state.question}</div>
          {isResolved && state.resolvedWith && (
            <div className="mt-1 text-xs italic opacity-75">
              You picked: {state.resolvedWith}
            </div>
          )}
        </div>
        {!isResolved && (
          <button
            type="button"
            onClick={onDismiss}
            className="btn-ghost p-1 text-amber-900 dark:text-amber-100"
            title="Dismiss"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {!isResolved && state.options.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-amber-200 px-3 py-2 dark:border-amber-900/60">
          {state.options.map((o) => (
            <button
              key={o.id}
              type="button"
              disabled={disabled}
              onClick={() => onPick(o.interpretation)}
              className="btn-primary text-xs"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
